import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { SHAPE_CATEGORIES, SHAPE_GEOMETRY, getRecentShapes, getShapeLabel } from '../../lib/shapes';
import { ShapeSvg } from './ObjectRenderer';
import { Type } from 'lucide-react';

export const ShapeThumb = ({ id, size = 22 }) => {
  if (id === 'textbox') return <span className="flex items-center justify-center border border-current rounded-[2px]" style={{ width: size - 4, height: size - 4 }}><Type className="w-3 h-3" /></span>;
  const isLine = SHAPE_GEOMETRY[id]?.kind === 'line' || SHAPE_GEOMETRY[id]?.open;
  return (
    <span style={{ width: size, height: size, display: 'inline-block' }}>
      <ShapeSvg shapeType={id} id={`thumb-${id}`} width={size} height={size} props={{ fill: isLine ? { type: 'none' } : { type: 'solid', color: 'currentColor' }, stroke: { color: 'currentColor', width: isLine ? 2 : 0 }, opacity: 100 }} />
    </span>
  );
};

/**
 * Insert ▸ Shapes gallery. Reused by Crop to Shape with `categories` (a reduced catalogue), `quick` (a first row of
 * picks instead of the recently-used shapes), `selected` (highlighted id), `title` and `footer` (extra actions).
 */
export const ShapesGallery = ({ onPick, children, align = 'start', categories = SHAPE_CATEGORIES, quick, selected, title, footer, testId = 'shapes-gallery', side }) => {
  const [open, setOpen] = useState(false);
  const recent = quick ? quick : getRecentShapes();
  const pick = (id) => { onPick(id); setOpen(false); };
  const cell = (id, label) => (
    <button key={id} type="button" title={label} onClick={() => pick(id)} aria-pressed={selected === id || undefined} className={`h-8 w-8 rounded-sm flex items-center justify-center transition-colors ${selected === id ? 'bg-primary/20 text-primary ring-1 ring-primary/50' : 'text-foreground/85 hover:bg-primary/15 hover:text-primary'}`} data-testid={`${testId}-item-${id}`}>
      <ShapeThumb id={id} />
    </button>
  );
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} side={side} className="w-[420px] max-w-[calc(100vw-16px)] max-h-[70vh] overflow-y-auto p-2 bg-popover text-popover-foreground border border-border shadow-lg z-[90]" data-ribbon onOpenAutoFocus={(e) => e.preventDefault()} data-testid={testId}>
        {title && <div className="text-[12px] font-semibold px-1 mb-1.5" data-testid={`${testId}-title`}>{title}</div>}
        {recent.length > 0 && (
          <div className="mb-2">
            <div className="text-[12px] font-semibold px-1 mb-1">{quick ? 'Quick picks' : 'Recently Used Shapes'}</div>
            <div className="grid grid-cols-12 gap-px">{recent.map((id) => cell(id, getShapeLabel(id)))}</div>
          </div>
        )}
        {categories.map((cat) => (
          <div key={cat.id} className="mb-2">
            <div className="text-[12px] font-semibold px-1 mb-1 border-t border-border pt-1.5">{cat.label}</div>
            <div className="grid grid-cols-12 gap-px">{cat.shapes.map((s) => cell(s.id, s.label))}</div>
          </div>
        ))}
        {footer && <div className="border-t border-border pt-1.5 mt-1" onClick={() => setOpen(false)}>{footer}</div>}
      </PopoverContent>
    </Popover>
  );
};
